import { Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import TrustScoreRing from '../components/TrustScoreRing';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useProfile } from '../contexts/ProfileContext';

/**
 * Dedicated Trust Score breakdown page.
 *
 * Phase B will add verification history here. For now we render the ring,
 * the tier label, the full component breakdown, and the disclaimer that this
 * is NOT a credit score.
 */
const TrustScore = () => {
  const { bundle, loading } = useProfile();
  const navigate = useNavigate();

  if (loading || !bundle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const score = bundle.trust_score;

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="trust-score-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        <div className="text-center pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
            Your Trust Score
          </p>
          <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">
            Trust Score is a Sturent-only signal that helps landlords see who you are at a glance.
            It is not a credit score and is not endorsed by any credit bureau.
          </p>
        </div>

        <Card className="bg-card-cream border-amber-200/60 shadow-soft">
          <CardContent className="p-6 text-center">
            <div className="inline-flex">
              <TrustScoreRing score={score.score} tier={score.tier} size={140} strokeWidth={12} />
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Higher Trust Scores get faster landlord replies.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200/70 shadow-soft">
          <CardContent className="p-5">
            <h2 className="font-heading font-bold text-base text-slate-900 mb-3">
              What's contributing
            </h2>
            <ul className="space-y-2" data-testid="trust-breakdown-list">
              {score.components.map((c) => (
                <li
                  key={c.key}
                  className={`flex items-center justify-between py-1.5 px-2 rounded ${
                    c.present ? 'bg-emerald-50' : ''
                  }`}
                >
                  <span className={`text-sm ${c.present ? 'text-emerald-800 font-medium' : 'text-slate-700'}`}>
                    {c.present ? '✓ ' : ''}
                    {c.label}
                  </span>
                  <span className={`text-xs ${c.present ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                    +{c.weight}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            className="btn-primary px-6 py-6 text-base"
            onClick={() => navigate('/profile/edit')}
            data-testid="trust-edit-profile-btn"
          >
            Edit profile
          </Button>
          <Button
            variant="outline"
            className="px-6 py-6 text-base border-slate-300"
            onClick={() => navigate('/profile/documents')}
            data-testid="trust-documents-btn"
          >
            Upload documents
          </Button>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Questions about Trust Score?{' '}
          <Link to="/help" className="text-amber-600 hover:underline">
            Read the FAQ
          </Link>
          .
        </p>
      </main>
    </div>
  );
};

export default TrustScore;
